import React, { useMemo, useState, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import './RegistrationDataGrid.css';
import LesionClinicalPanel from './LesionClinicalPanel';
import {
    formatRegistrationHeaderName,
    orderTableColumnKeys,
} from '../../utils/registrationTableColumns';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Generate visually distinct colors using HSL color space, return as hex
const generateDistinctColor = (index, total = 20, saturation = 85, lightness = 50) => {
    // Distribute hue values evenly around the color wheel for maximum distinction
    const hue = (index * 360 / total) % 360;

    // Convert HSL to RGB
    const s = saturation / 100;
    const l = lightness / 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
    const m = l - c / 2;

    let r = 0, g = 0, b = 0;
    if (hue >= 0 && hue < 60) {
        r = c; g = x; b = 0;
    } else if (hue >= 60 && hue < 120) {
        r = x; g = c; b = 0;
    } else if (hue >= 120 && hue < 180) {
        r = 0; g = c; b = x;
    } else if (hue >= 180 && hue < 240) {
        r = 0; g = x; b = c;
    } else if (hue >= 240 && hue < 300) {
        r = x; g = 0; b = c;
    } else {
        r = c; g = 0; b = x;
    }

    // Convert to 0-255 range and then to hex
    const toHex = (value) => {
        const hex = Math.round((value + m) * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Color picker component for AG Grid
const ColorPicker = (params) => {
    const rowIndex = params.rowIndex;
    const data = params.data;
    const currentColor = data?.roiColor || '#00ff00';

    const handleColorChange = (event) => {
        const newColor = event.target.value;
        // Update the data
        data.roiColor = newColor;
        // Trigger a refresh of the grid
        params.api.refreshCells({ rowNodes: [params.node], columns: ['roiColor'] });

        // Notify parent component about color change
        if (params.onColorChange) {
            params.onColorChange(rowIndex, newColor);
        }
    };

    // Prevent row selection when clicking on color picker
    const handleMouseDown = (e) => {
        e.stopPropagation();
    };

    const handleClick = (e) => {
        e.stopPropagation();
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '2px'
            }}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
        >
            <input
                type="color"
                value={currentColor}
                onChange={handleColorChange}
                style={{
                    width: '24px',
                    height: '24px',
                    border: '1px solid #333',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    padding: 0
                }}
                title={`ROI ${rowIndex + 1} Color`}
            />
        </div>
    );
};

// Pan to ROI component for AG Grid
const PanToROI = (params) => {
    const data = params.data;
    const originalIndex = data.originalIndex; // Use original index from registration data

    // Check if has valid coordinates to pan to
    const hasValidCoords = data.x_min !== data.x_max && data.y_min !== data.y_max &&
        data.x_min != null && data.x_max != null &&
        data.y_min != null && data.y_max != null &&
        data.x_min !== 'null' && data.x_max !== 'null' &&
        data.y_min !== 'null' && data.y_max !== 'null';
    const isDisabled = !hasValidCoords;

    const handlePan = () => {
        if (isDisabled) return;

        console.log(`🎯 Pan to ROI ${originalIndex} clicked`);

        // Notify parent component about pan request (use original index!)
        if (params.onPanToROI && originalIndex !== undefined && !isNaN(originalIndex)) {
            console.log(`🔄 Calling onPanToROI for ROI ${originalIndex}`);
            params.onPanToROI(originalIndex, data);
        }
    };

    // Prevent row selection when clicking
    const handleMouseDown = (e) => {
        e.stopPropagation();
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!isDisabled) {
            handlePan();
        }
    };

    const getTooltip = () => {
        if (!hasValidCoords) return 'Missing coordinate data';
        return `Pan to ROI ${originalIndex + 1}`;
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '2px 8px'
            }}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
        >
            <button
                disabled={isDisabled}
                style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #dadce0',
                    borderRadius: '4px',
                    backgroundColor: isDisabled ? '#e0e0e0' : '#FF9800',
                    color: isDisabled ? '#9e9e9e' : 'white',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    transition: 'all 0.2s ease',
                    opacity: isDisabled ? 0.5 : 1
                }}
                title={getTooltip()}
            >
                {isDisabled ? '❌' : '🎯'}
            </button>
        </div>
    );
};

// Visibility toggle component for AG Grid
const VisibilityToggle = (params) => {
    const data = params.data;
    const originalIndex = data.originalIndex; // Use original index from registration data
    const [isVisible, setIsVisible] = useState(data?.roiVisible !== undefined ? data.roiVisible : true);

    // Show button only needs valid coordinates (it controls ROI box visibility, not image overlay)
    // Note: values are converted to strings, so null becomes "null"
    const hasValidCoords = data.x_min !== data.x_max && data.y_min !== data.y_max &&
        data.x_min != null && data.x_max != null &&
        data.y_min != null && data.y_max != null &&
        data.x_min !== 'null' && data.x_max !== 'null' &&
        data.y_min !== 'null' && data.y_max !== 'null';
    const isDisabled = !hasValidCoords;

    const handleToggle = () => {
        if (isDisabled) return;

        const newVisibility = !isVisible;
        console.log(`🔘 Toggle clicked for ROI ${originalIndex}: ${isVisible} -> ${newVisibility}`);
        console.log(`🔘 params.onVisibilityChange exists?`, !!params.onVisibilityChange);
        console.log(`🔘 Full params:`, params);

        // Update local state
        setIsVisible(newVisibility);

        // Update the data
        data.roiVisible = newVisibility;

        // Notify parent component about visibility change (use original index!)
        if (params.onVisibilityChange && originalIndex !== undefined && !isNaN(originalIndex)) {
            console.log(`🔄 Calling onVisibilityChange for ROI ${originalIndex}:`, newVisibility);
            params.onVisibilityChange(originalIndex, newVisibility);
        } else {
            console.warn('❌ Invalid originalIndex or missing callback:', { originalIndex, hasCallback: !!params.onVisibilityChange });
        }
    };

    // Prevent row selection when clicking on toggle
    const handleMouseDown = (e) => {
        e.stopPropagation();
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!isDisabled) {
            handleToggle();
        }
    };

    const getTooltip = () => {
        if (!hasValidCoords) return 'Missing coordinate data';
        return `ROI ${originalIndex + 1}: ${isVisible ? 'Visible' : 'Hidden'}`;
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '2px 8px'
            }}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
        >
            <button
                disabled={isDisabled}
                style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #dadce0',
                    borderRadius: '4px',
                    backgroundColor: isDisabled ? '#e0e0e0' : (isVisible ? '#4CAF50' : '#f44336'),
                    color: isDisabled ? '#9e9e9e' : 'white',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    transition: 'all 0.2s ease',
                    opacity: isDisabled ? 0.5 : 1
                }}
                title={getTooltip()}
            >
                {isDisabled ? '❌' : (isVisible ? '👁️' : '🚫')}
            </button>
        </div>
    );
};

// Tiled overlay toggle component for AG Grid
const TiledOverlayToggle = (params) => {
    const data = params.data;
    const originalIndex = data.originalIndex; // Use original index from registration data
    const [isOverlayActive, setIsOverlayActive] = useState(data?.tiledOverlayActive !== undefined ? data.tiledOverlayActive : false);

    // Check if image exists in DSA (has yamlId) and has valid coordinates
    // Note: values are converted to strings, so null becomes "null"
    const hasImage = !!(data.yamlId && data.yamlId !== 'null' && data.yamlId !== 'undefined') ||
        !!(data._id && data._id !== 'null' && data._id !== 'undefined');
    const hasValidCoords = data.x_min !== data.x_max && data.y_min !== data.y_max &&
        data.x_min != null && data.x_max != null &&
        data.y_min != null && data.y_max != null &&
        data.x_min !== 'null' && data.x_max !== 'null' &&
        data.y_min !== 'null' && data.y_max !== 'null';
    const isDisabled = !hasImage || !hasValidCoords;

    const handleToggle = () => {
        if (isDisabled) return;

        const newState = !isOverlayActive;
        console.log(`🖼️ Overlay toggle clicked for ROI ${originalIndex}: ${isOverlayActive} -> ${newState}`);

        // Update local state
        setIsOverlayActive(newState);

        // Update the data
        data.tiledOverlayActive = newState;

        // Notify parent component about overlay toggle (use original index!)
        if (params.onOverlayToggle && originalIndex !== undefined && !isNaN(originalIndex)) {
            console.log(`🔄 Calling onOverlayToggle for ROI ${originalIndex}:`, newState);
            params.onOverlayToggle(originalIndex, newState);
        }
    };

    // Prevent row selection when clicking on toggle
    const handleMouseDown = (e) => {
        e.stopPropagation();
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!isDisabled) {
            handleToggle();
        }
    };

    const getTooltip = () => {
        if (!hasImage) return 'Image not available in DSA';
        if (!hasValidCoords) return 'Missing coordinate data';
        return `Tiled overlay ${originalIndex + 1}: ${isOverlayActive ? 'Active' : 'Inactive'}`;
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '2px 8px'
            }}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
        >
            <button
                disabled={isDisabled}
                style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #dadce0',
                    borderRadius: '4px',
                    backgroundColor: isDisabled ? '#e0e0e0' : (isOverlayActive ? '#2196F3' : '#9E9E9E'),
                    color: isDisabled ? '#9e9e9e' : 'white',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    transition: 'all 0.2s ease',
                    opacity: isDisabled ? 0.5 : 1
                }}
                title={getTooltip()}
            >
                {isDisabled ? '❌' : (isOverlayActive ? '🖼️' : '📄')}
            </button>
        </div>
    );
};

const RegistrationDataGrid = ({
    selectedFolder,
    selectedRegistrationIndex,
    onImageSelect,
    onVisibilityChange,
    onOverlayToggle,
    onPanToROI,
    onCoordinateChange,
    layoutMode = 'full',
    registrationLayout = 'bottom',
    onRegistrationLayoutChange,
}) => {
    const isNavigationMode = layoutMode === 'navigation';
    const [selectedTheme, setSelectedTheme] = useState('ag-theme-alpine');
    const gridRef = useRef(null);

    // Theme options using legacy CSS themes
    const themeOptions = [
        { value: 'ag-theme-alpine', label: 'Alpine (Default)' },
        { value: 'ag-theme-balham', label: 'Balham (Dark)' },
        { value: 'ag-theme-material', label: 'Material Design' }
    ];

    // Extract registration data from the folder
    const getRegistrationData = (folder) => {
        if (!folder || !folder.meta || !folder.meta.registrationData) {
            return null;
        }
        return folder.meta.registrationData;
    };

    const registrationData = getRegistrationData(selectedFolder);

    // Convert registration data to row data for ag-grid
    const rowData = useMemo(() => {
        if (!registrationData) return [];

        // Handle both array and object formats
        if (Array.isArray(registrationData)) {
            // Simply map all items without filtering
            return registrationData.map((item, index) => {
                const row = {
                    index: `Item ${index + 1}`,
                    roiColor: generateDistinctColor(index),
                    roiVisible: true,
                    originalIndex: index // Simple 1:1 mapping
                };

                // Flatten the object properties into columns for display
                Object.entries(item).forEach(([key, value]) => {
                    row[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
                });

                return row;
            });
        } else {
            return Object.entries(registrationData).map(([key, value]) => {
                const row = { property: key };
                if (typeof value === 'object' && value !== null) {
                    Object.entries(value).forEach(([subKey, subValue]) => {
                        row[subKey] = typeof subValue === 'object' ? JSON.stringify(subValue) : String(subValue);
                    });
                } else {
                    row.value = String(value);
                }
                return row;
            });
        }
    }, [registrationData]);

    // Column definitions for ag-grid
    const columnDefs = useMemo(() => {
        if (!registrationData || !Array.isArray(registrationData) || registrationData.length === 0) {
            return [];
        }

        // Get all unique keys from all registration data items
        const allKeys = new Set();
        registrationData.forEach(item => {
            if (typeof item === 'object' && item !== null) {
                Object.keys(item).forEach(key => allKeys.add(key));
            }
        });


        // Create columns dynamically
        const iconColumnWidth = isNavigationMode ? 36 : 50;
        const columns = [
            {
                field: 'index',
                headerName: 'Index',
                width: 100,
                pinned: 'left',
                hide: true, // Hide the index column
                cellStyle: {
                    fontWeight: '500',
                    backgroundColor: '#f8f9fa'
                }
            }
        ];

        // Add color picker column first
        columns.push({
            field: 'roiColor',
            headerName: '',
            width: iconColumnWidth,
            maxWidth: iconColumnWidth,
            filter: false,
            sortable: false,
            resizable: false,
            suppressSizeToFit: true,
            cellRenderer: ColorPicker,
            cellRendererParams: {
                onColorChange: (rowIndex, newColor) => {
                    console.log(`ROI ${rowIndex + 1} color changed to:`, newColor);
                    // You can add logic here to update the ROI colors on the viewer
                }
            }
        });

        // Add pan to ROI column
        columns.push({
            field: 'panToROI',
            headerName: '',
            width: iconColumnWidth,
            maxWidth: iconColumnWidth,
            filter: false,
            sortable: false,
            resizable: false,
            suppressSizeToFit: true,
            cellRenderer: PanToROI,
            cellRendererParams: {
                onPanToROI: (rowIndex, data) => {
                    console.log(`Pan to ROI ${rowIndex + 1}:`, data);
                    // Pass pan request to parent component
                    if (onPanToROI) {
                        onPanToROI(rowIndex, data);
                    }
                }
            }
        });

        // Add visibility toggle column
        columns.push({
            field: 'roiVisible',
            headerName: '',
            width: iconColumnWidth,
            maxWidth: iconColumnWidth,
            filter: false,
            sortable: false,
            resizable: false,
            suppressSizeToFit: true,
            cellRenderer: VisibilityToggle,
            cellRendererParams: {
                onVisibilityChange: (rowIndex, newVisibility) => {
                    console.log(`ROI ${rowIndex + 1} visibility changed to:`, newVisibility);
                    // Pass visibility change to parent component
                    if (onVisibilityChange) {
                        onVisibilityChange(rowIndex, newVisibility);
                    }
                }
            }
        });

        // Add tiled overlay toggle column
        columns.push({
            field: 'tiledOverlay',
            headerName: '',
            width: iconColumnWidth,
            maxWidth: iconColumnWidth,
            filter: false,
            sortable: false,
            resizable: false,
            suppressSizeToFit: true,
            cellRenderer: TiledOverlayToggle,
            cellRendererParams: {
                onOverlayToggle: (rowIndex, isActive) => {
                    console.log(`Tiled overlay ${rowIndex + 1} toggled to:`, isActive);
                    // Pass overlay toggle to parent component
                    if (onOverlayToggle) {
                        onOverlayToggle(rowIndex, isActive);
                    }
                }
            }
        });

        if (isNavigationMode) {
            columns.push({
                field: 'itemid',
                headerName: 'Item ID',
                flex: 1,
                minWidth: 72,
                filter: false,
                sortable: true,
                cellStyle: {
                    fontFamily: 'Courier New, monospace',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    padding: '2px 4px',
                },
                tooltipField: 'itemid',
            });
            return columns;
        }

        // Add image-specific columns in registration-friendly order
        const orderedKeys = orderTableColumnKeys(allKeys);
        orderedKeys.forEach((key) => {
            const isEditableCoord = ['x_min', 'x_max', 'y_min', 'y_max'].includes(key);

            columns.push({
                field: key,
                headerName: formatRegistrationHeaderName(key),
                width: key === 'itemid' ? 200 : 130,
                editable: isEditableCoord,
                cellStyle: {
                    fontFamily: 'Courier New, monospace',
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    padding: '4px 8px',
                    ...(isEditableCoord ? { backgroundColor: '#fffbf0' } : {}),
                },
                valueParser: isEditableCoord ? (params) => {
                    const newValue = parseFloat(params.newValue);
                    return Number.isNaN(newValue) ? params.oldValue : newValue;
                } : undefined,
                autoHeight: false,
                wrapText: true,
            });
        });

        return columns;
    }, [registrationData, onPanToROI, onVisibilityChange, onOverlayToggle, isNavigationMode]);

    // Default column properties
    const defaultColDef = useMemo(() => ({
        resizable: !isNavigationMode,
        sortable: !isNavigationMode,
        filter: !isNavigationMode,
        wrapText: !isNavigationMode,
        autoHeight: false,
        cellStyle: isNavigationMode
            ? { fontSize: '12px', padding: '2px 4px' }
            : { fontSize: '14px', padding: '4px 8px' },
    }), [isNavigationMode]);

    if (!registrationData) {
        return (
            <div className="registration-data-grid">
                <h3>Registration Data</h3>
                <p className="no-data">No registration data available for the selected folder.</p>
            </div>
        );
    }


    return (
        <div className={`registration-data-grid${isNavigationMode ? ' registration-data-grid--navigation' : ''}`}>
            <div className="grid-header">
                <h3>{isNavigationMode ? `${rowData.length} items` : `Registration Data (${rowData.length} items)`}</h3>
                <div className="grid-controls">
                    {onRegistrationLayoutChange && (
                        <div className="registration-layout-toggle" role="group" aria-label="Registration panel layout">
                            <button
                                type="button"
                                className={`registration-layout-toggle-btn${
                                    registrationLayout === 'right' ? ' registration-layout-toggle-btn--active' : ''
                                }`}
                                onClick={() => onRegistrationLayoutChange('right')}
                                title="Slim navigation panel on the right"
                            >
                                Right nav
                            </button>
                            <button
                                type="button"
                                className={`registration-layout-toggle-btn${
                                    registrationLayout === 'bottom' ? ' registration-layout-toggle-btn--active' : ''
                                }`}
                                onClick={() => onRegistrationLayoutChange('bottom')}
                                title="Full registration table at the bottom"
                            >
                                Bottom
                            </button>
                        </div>
                    )}
                    {!isNavigationMode && (
                        <div className="theme-selector">
                            <label htmlFor="theme-select">Theme:</label>
                            <select
                                id="theme-select"
                                value={selectedTheme}
                                onChange={(e) => setSelectedTheme(e.target.value)}
                                className="theme-dropdown"
                            >
                                {themeOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>
            <div className={`registration-panel-split${isNavigationMode ? ' registration-panel-split--navigation' : ''}`}>
                <div className={`grid-container ${selectedTheme}`}>
                    <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={true}
                    suppressRowClickSelection={false}
                    suppressHorizontalScroll={isNavigationMode}
                    headerHeight={isNavigationMode ? 28 : undefined}
                    rowHeight={isNavigationMode ? 36 : undefined}
                    getRowStyle={(params) => {
                        const originalIndex = params.data?.originalIndex;
                        const isSelected = (
                            selectedRegistrationIndex != null
                            && originalIndex === selectedRegistrationIndex
                        );
                        const type = params.data.type?.toLowerCase() || '';
                        const itemid = params.data.itemid?.toLowerCase() || '';
                        const description = params.data.description?.toLowerCase() || '';

                        let style;
                        if (type.includes('macroscopic') ||
                            itemid.includes('macroscopic') ||
                            description.includes('macroscopic')) {
                            style = {
                                opacity: '0.5',
                                pointerEvents: 'none',
                                backgroundColor: '#f5f5f5',
                            };
                        } else if (type.includes('dermoscopic')) {
                            style = {
                                backgroundColor: '#e8f4fd',
                                cursor: 'pointer',
                            };
                        }

                        if (isSelected) {
                            return {
                                ...(style || {}),
                                boxShadow: 'inset 0 0 0 2px #2563eb',
                                fontWeight: '600',
                            };
                        }

                        return style;
                    }}
                    rowSelection="single"
                    pagination={false}
                    theme="legacy"
                    rowHeight={32}
                    onGridReady={(params) => {
                        // Auto-size all columns to fit content
                        params.api.autoSizeAllColumns();
                    }}
                    onCellClicked={(event) => {
                        console.log('Cell clicked:', { column: event.column.colId, data: event.data });

                        // Only trigger image selection when clicking on specific columns
                        // Exclude interactive columns like roiColor, roiOpacity, and index
                        const interactiveColumns = [
                            'roiColor',
                            'panToROI',
                            'roiVisible',
                            'tiledOverlay',
                            'index',
                        ];
                        const clickedColumn = event.column.colId;

                        if (interactiveColumns.includes(clickedColumn)) {
                            console.log('Clicked on interactive column, skipping image selection');
                            return;
                        }

                        if (onImageSelect && event.data && event.data.originalIndex !== undefined) {
                            // Simple approach: look up the item from the original registration data
                            const originalIndex = event.data.originalIndex;
                            const fullRegistrationData = selectedFolder?.meta?.registrationData;

                            if (fullRegistrationData && Array.isArray(fullRegistrationData)) {
                                const selectedItem = fullRegistrationData[originalIndex];
                                if (selectedItem) {
                                    console.log('✅ Selected item:', {
                                        displayRow: event.data.index,
                                        originalIndex: originalIndex,
                                        itemId: selectedItem.itemid,
                                        description: selectedItem.description,
                                        type: selectedItem.type
                                    });
                                    onImageSelect(selectedItem, originalIndex);
                                } else {
                                    console.warn('Could not find item at index:', originalIndex);
                                }
                            }
                        }
                    }}
                    onCellValueChanged={(event) => {
                        console.log('Cell value changed:', { column: event.column.colId, newValue: event.newValue, oldValue: event.oldValue });

                        // Check if a coordinate column was changed
                        const coordColumns = ['x_min', 'x_max', 'y_min', 'y_max'];
                        if (coordColumns.includes(event.column.colId)) {
                            const originalIndex = event.data.originalIndex;
                            console.log(`📝 Coordinate changed for ROI ${originalIndex}:`, {
                                field: event.column.colId,
                                oldValue: event.oldValue,
                                newValue: event.newValue
                            });

                            // Update the actual registration data in the folder metadata
                            if (selectedFolder?.meta?.registrationData && originalIndex !== undefined) {
                                selectedFolder.meta.registrationData[originalIndex][event.column.colId] = event.newValue;
                            }

                            // Notify parent to redraw ROIs
                            if (onCoordinateChange) {
                                onCoordinateChange(originalIndex, event.column.colId, event.newValue);
                            }
                        }
                    }}
                />
                </div>
                {!isNavigationMode && (
                    <LesionClinicalPanel
                        registrationData={registrationData}
                        selectedRegistrationIndex={selectedRegistrationIndex}
                        folderMeta={selectedFolder?.meta}
                    />
                )}
            </div>
        </div>
    );
};

export default RegistrationDataGrid;
